import { Controller } from '@hotwired/stimulus'

// Mounted on <body>. Intercepts all input events and strips characters that
// don't match the field's existing `pattern` attribute, enforcing the same
// rules the browser would check on submit — but in real time as the user types.
//
// Supported pattern shapes (as they appear in the DOM attribute):
//   \d*              → digits only
//   \d{N}            → digits only (fixed length)
//   \d*(\.\d+)?      → decimal number (digits + optional . + digits)
//   [+\-]?\d*(\.\d+)? → signed decimal (optional leading +/-)
//   [\d/]*            → vision (digits and / only, e.g. 20/20 or 20)
export default class extends Controller {
  connect () {
    this._onInput = this._handleInput.bind(this)
    this.element.addEventListener('input', this._onInput, true)
  }

  disconnect () {
    this.element.removeEventListener('input', this._onInput, true)
  }

  _handleInput (event) {
    const el = event.target
    if (el.tagName !== 'INPUT' || el.type === 'hidden' || el.readOnly || !el.pattern) return

    const type = this._classify(el.pattern)
    if (!type) return

    const before = el.value
    const cursor = el.selectionStart
    const cleaned = this._clean(before, type)
    if (cleaned === before) return

    el.value = cleaned
    const newPos = Math.max(0, cursor - (before.length - cleaned.length))
    el.setSelectionRange(newPos, newPos)
  }

  _classify (pattern) {
    if (/^\\d(\*|\{\d+\})$/.test(pattern)) return 'integer'              // \d* or \d{N}
    if (pattern === '\\d*(\\.\\d+)?') return 'decimal'                   // \d*(\.\d+)?
    if (pattern === '[+\\-]?\\d*(\\.\\d+)?') return 'signed'             // [+\-]?\d*(\.\d+)?
    if (pattern === '[\\d/]*') return 'vision'                            // [\d/]*
    return null
  }

  _clean (value, type) {
    switch (type) {
      case 'integer':
        return value.replace(/\D/g, '')

      case 'decimal': {
        const v = value.replace(/[^\d.]/g, '')
        const dot = v.indexOf('.')
        return dot < 0 ? v : v.slice(0, dot + 1) + v.slice(dot + 1).replace(/\./g, '')
      }

      case 'signed': {
        const sign = /^[+\-]/.test(value) ? value[0] : ''
        const rest = value.slice(sign ? 1 : 0).replace(/[^\d.]/g, '')
        const dot = rest.indexOf('.')
        const num = dot < 0 ? rest : rest.slice(0, dot + 1) + rest.slice(dot + 1).replace(/\./g, '')
        return sign + num
      }

      case 'vision':
        return value.replace(/[^\d/]/g, '')

      default:
        return value
    }
  }
}
